import { createContext, type ReactNode, useMemo, useReducer } from "react";
import { RefineCategoryPageIdProps } from "src/hooks/algolia/useRefineCategoryPageId";
import { CategoryNodeType } from "src/molecules/CategoryTree";
import { TreeType } from "src/types/Tree";
import { createReducer, type Dispatch } from "src/utils/create-reducer";

export type CategoryTreeState = {
  tree: TreeType<CategoryNodeType>;
  items: string[];
  refine: (props: RefineCategoryPageIdProps) => void;
  initialRefine: (categories: string[]) => void;
  rootIds: string[];
  parentIds: string[];
  isLoading: boolean;
  categoryPageId?: string;
  selectedCategories: string[];
};

const initialState: CategoryTreeState = {
  tree: [],
  items: [],
  refine: () => {},
  initialRefine: () => {},
  rootIds: [],
  parentIds: [],
  isLoading: true,
  categoryPageId: undefined,
  selectedCategories: [],
};

export type CategoryTreeAction =
  | {
      type: "SET_TREE";
      payload: TreeType<CategoryNodeType>;
    }
  | {
      type: "SET_ITEMS";
      payload: string[];
    }
  | {
      type: "SET_LOADING";
      payload: boolean;
    }
  | {
      type: "SET_INITIAL_STATE";
      payload: {
        tree: TreeType<CategoryNodeType>;
        categoryPageId?: string;
        isLoading?: boolean;
        canFilterCategoryPageId: boolean;
      };
    }
  | {
      type: "SET_SELECTED_CATEGORIES";
      payload: string[];
    }
  | {
      type: "SET_REFINE";
      payload: (props: RefineCategoryPageIdProps) => void;
    }
  | {
      type: "SET_INITIAL_REFINE";
      payload: (categories: string[]) => void;
    };

export type UseCategoryTreeContextReturn = {
  treeState: CategoryTreeState;
  treeDispatch: Dispatch<CategoryTreeAction>;
};

export const CategoryTreeContext = createContext<CategoryTreeState | undefined>(
  undefined
);

export const CategoryTreeDispatch = createContext<
  Dispatch<CategoryTreeAction> | undefined
>(undefined);

export const treeReducer = createReducer<CategoryTreeState, CategoryTreeAction>(
  {
    SET_TREE: (state, payload) => {
      return { ...state, tree: payload };
    },
    SET_ITEMS: (state, payload) => {
      return { ...state, items: payload };
    },
    SET_LOADING: (state, payload) => {
      return { ...state, isLoading: payload };
    },
    SET_SELECTED_CATEGORIES: (state, payload) => {
      return { ...state, selectedCategories: payload };
    },
    SET_INITIAL_STATE: (
      state,
      { tree, categoryPageId, isLoading, canFilterCategoryPageId }
    ) => {
      const genderIds = tree.flatMap((item) =>
        item.parentId === "0" ? [item.itemId] : []
      );

      const rootIds = tree.flatMap((category) => {
        if (
          (canFilterCategoryPageId &&
            category.categoryPageId === categoryPageId) ||
          (!canFilterCategoryPageId && genderIds.includes(category.parentId))
        ) {
          return [category.itemId];
        }

        return [];
      });

      const parentIds = tree.flatMap((category) => {
        if (rootIds.includes(category.parentId)) {
          return [category.itemId];
        }

        return [];
      });

      return {
        ...state,
        tree,
        parentIds,
        rootIds,
        isLoading: isLoading || false,
        categoryPageId,
      };
    },
    SET_INITIAL_REFINE: (state, payload) => {
      return { ...state, initialRefine: payload };
    },
    SET_REFINE: (state, payload) => {
      return { ...state, refine: payload };
    },
  }
);

export function CategoryTreeProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [treeState, treeDispatch] = useReducer(treeReducer, initialState);

  const treeValue = useMemo(() => treeState, [treeState]);

  return (
    <CategoryTreeContext.Provider value={treeValue}>
      <CategoryTreeDispatch.Provider value={treeDispatch}>
        {children}
      </CategoryTreeDispatch.Provider>
    </CategoryTreeContext.Provider>
  );
}
